import { useState, useEffect, useCallback } from 'react';
import { convertImagePathToBlob, revokeBlobUrls } from '../utils/imageUtils';

/**
 * Custom hook to manage image blob URLs
 * @param {string} imagePath - The server path to the image
 * @param {string} apiUrl - The API base URL
 * @returns {Object} - Object containing blobUrl, loading state, and error state
 */
export const useImageBlob = (imagePath, apiUrl) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadImage = useCallback(async () => {
    if (!imagePath || !apiUrl) {
      setBlobUrl(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = await convertImagePathToBlob(imagePath, apiUrl);
      setBlobUrl(url);
    } catch (err) {
      setError(err.message);
      console.error('Error loading image:', err);
    } finally {
      setLoading(false);
    }
  }, [imagePath, apiUrl]);

  useEffect(() => {
    loadImage();

    // Cleanup function to revoke blob URL when component unmounts or image changes
    return () => {
      if (blobUrl) {
        revokeBlobUrls(blobUrl);
      }
    };
  }, [imagePath, apiUrl]);

  return { blobUrl, loading, error, reload: loadImage };
};

/**
 * Custom hook to manage multiple image blob URLs
 * @param {string[]} imagePaths - Array of server paths to images
 * @param {string} apiUrl - The API base URL
 * @returns {Object} - Object containing blobUrls array, loading state, and error state
 */
export const useMultipleImageBlobs = (imagePaths, apiUrl) => {
  const [blobUrls, setBlobUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadImages = useCallback(async () => {
    if (!imagePaths || imagePaths.length === 0 || !apiUrl) {
      setBlobUrls([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const urls = await Promise.all(
        imagePaths.map((path) => convertImagePathToBlob(path, apiUrl))
      );
      setBlobUrls(urls.filter((url) => url !== null));
    } catch (err) {
      setError(err.message);
      console.error('Error loading images:', err);
    } finally {
      setLoading(false);
    }
  }, [imagePaths, apiUrl]);

  useEffect(() => {
    loadImages();

    // Cleanup function to revoke blob URLs when component unmounts or images change
    return () => {
      if (blobUrls.length > 0) {
        revokeBlobUrls(blobUrls);
      }
    };
  }, [imagePaths, apiUrl]);

  return { blobUrls, loading, error, reload: loadImages };
};
